import { useState, useRef, useEffect } from 'react';
import { ArrowUpIcon, SpinnerIcon, ChevronUpIcon, SparklesIcon, RocketIcon, XMarkIcon, DocumentIcon, ClipboardIcon, BoltIcon, PencilIcon } from '../ui/icons';
import type { ChatMode, GenerationModel } from '../../store/settings';
import { useSettingsStore } from '../../store/settings';
import { useCreditsStore } from '../../store/credits';
import { TIER_LIMITS, type SubscriptionTier } from '@n8n-builder/shared';
import type { Workflow } from '@n8n-builder/n8n-types';
import type { AttachedWorkflow } from '../../store/chat';
import { validateWorkflowJson } from '../../utils/workflow-validation';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  disabled: boolean;
  isLoading: boolean;
  mode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
  model: GenerationModel;
  onModelChange: (model: GenerationModel) => void;
  workflow?: Workflow | null;
  workflowId?: string | null;
  onDeploy?: (workflow: Workflow, workflowId?: string) => void;
  isDeploying?: boolean;
  sidePanelOpen?: boolean;
  onStartNewChat?: () => void;
  attachedWorkflow?: AttachedWorkflow | null;
  onAttachWorkflow?: (attachment: AttachedWorkflow) => void;
  onRemoveAttachment?: () => void;
}

const BASE_MODE_OPTIONS: { value: ChatMode; label: string; description: string }[] = [
  { value: 'plan', label: 'Plan', description: 'Review plan before building' },
  { value: 'build', label: 'Build', description: 'Generate workflow directly' },
];

const EDIT_MODE_OPTION: { value: ChatMode; label: string; description: string } = {
  value: 'edit',
  label: 'Edit',
  description: 'Refine the current workflow',
};

const MODEL_OPTIONS: { value: GenerationModel; label: string; description: string }[] = [
  { value: 'sonnet-4.5', label: 'Sonnet 4.5', description: 'Balanced speed and quality' },
  { value: 'haiku-4.5', label: 'Haiku 4.5', description: 'Fast and efficient' },
  { value: 'opus-4.5', label: 'Opus 4.5', description: 'Highest quality' },
];

interface DropdownProps {
  value: string;
  options: { value: string; label: string; description: string }[];
  onChange: (value: string) => void;
  disabled: boolean;
  variant: 'filled-green' | 'filled-blue' | 'filled-purple' | 'outlined';
  icon: React.ReactNode;
  lockedOptions?: Set<string>;
  onLockedClick?: (value: string) => void;
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function Dropdown({ value, options, onChange, disabled, variant, icon, lockedOptions, onLockedClick }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseStyles = 'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    'filled-green': 'bg-green-100 text-green-700 hover:bg-green-200',
    'filled-blue': 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    'filled-purple': 'bg-purple-100 text-purple-700 hover:bg-purple-200',
    'outlined': 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
  }[variant];

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`${baseStyles} ${variantStyles}`}
      >
        {icon}
        <span className="hidden sm:inline">{selectedOption?.label}</span>
        <ChevronUpIcon className={`h-3 w-3 transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-50">
          {options.map((option) => {
            const isLocked = lockedOptions?.has(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (isLocked) {
                    onLockedClick?.(option.value);
                    setIsOpen(false);
                  } else {
                    onChange(option.value);
                    setIsOpen(false);
                  }
                }}
                className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${
                  option.value === value ? 'bg-primary-50' : ''
                } ${isLocked ? 'opacity-75' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">{option.label}</div>
                  {isLocked && (
                    <div className="flex items-center gap-1">
                      <LockIcon className="h-3 w-3 text-gray-400" />
                      <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">Business</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500">{isLocked ? 'Upgrade to unlock' : option.description}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Max file size for uploads (500KB)
const MAX_FILE_SIZE = 500 * 1024;

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled,
  isLoading,
  mode,
  onModeChange,
  model,
  onModelChange,
  workflow,
  workflowId,
  onDeploy,
  isDeploying,
  sidePanelOpen = false,
  onStartNewChat,
  attachedWorkflow,
  onAttachWorkflow,
  onRemoveAttachment,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const subscription = useCreditsStore((s) => s.subscription) as SubscriptionTier;
  const openSettingsModal = useSettingsStore((s) => s.openSettingsModal);

  // Compute locked models based on subscription tier
  const lockedModels = new Set(
    MODEL_OPTIONS
      .filter((opt) => !(TIER_LIMITS[subscription]?.allowedModels as readonly string[])?.includes(opt.value))
      .map((opt) => opt.value)
  );

  // Reset model if persisted selection is no longer allowed (e.g., after downgrade)
  useEffect(() => {
    if (lockedModels.has(model)) {
      onModelChange('sonnet-4.5');
    }
  }, [subscription]);

  // Compute mode options dynamically based on workflow presence
  const hasWorkflow = Boolean(workflow || attachedWorkflow);
  const modeOptions = hasWorkflow
    ? [...BASE_MODE_OPTIONS, EDIT_MODE_OPTION]
    : BASE_MODE_OPTIONS;

  // Handle mode change with new chat logic
  const handleModeChange = (newMode: ChatMode) => {
    // If we have a workflow and user selects plan/build, start new chat
    if (hasWorkflow && (newMode === 'plan' || newMode === 'build')) {
      onStartNewChat?.();
    }
    onModeChange(newMode);
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 200); // Max ~6 rows
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  // Handle Enter to submit (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) {
        onSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  const canSubmit = !disabled && value.trim();

  // Clear file error after 5 seconds
  useEffect(() => {
    if (fileError) {
      const timer = setTimeout(() => setFileError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [fileError]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be selected again
    e.target.value = '';

    // Check extension
    if (!file.name.toLowerCase().endsWith('.json')) {
      setFileError('Please select a .json file');
      return;
    }

    // Check size
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File too large. Maximum 500KB allowed.');
      return;
    }

    try {
      const content = await file.text();
      const result = validateWorkflowJson(content);

      if (!result.isValid) {
        setFileError(result.errors[0] || 'Invalid workflow file');
        return;
      }

      if (result.workflow && onAttachWorkflow) {
        onAttachWorkflow({
          filename: file.name,
          workflow: result.workflow,
          nodeCount: result.nodeCount || 0,
        });
        setFileError(null);
      }
    } catch {
      setFileError('Failed to read file');
    }
  };

  const handleDeploy = () => {
    if (workflow && onDeploy) {
      onDeploy(workflow, workflowId || undefined);
    }
  };

  // Determine mode dropdown variant and icon based on selected mode
  const modeVariant = mode === 'plan'
    ? 'filled-green'
    : mode === 'build'
      ? 'filled-blue'
      : 'filled-purple';

  const modeIcon = mode === 'plan'
    ? <ClipboardIcon className="h-4 w-4" />
    : mode === 'build'
      ? <BoltIcon className="h-4 w-4" />
      : <PencilIcon className="h-4 w-4" />;

  // Use wider layout when side panel is collapsed
  const maxWidthClass = sidePanelOpen ? 'max-w-3xl' : 'max-w-5xl';

  return (
    <form
      onSubmit={onSubmit}
      className="flex-shrink-0 px-3 py-3 md:px-6 md:py-4"
    >
      <div className={`mx-auto ${maxWidthClass}`}>
        <div className="flex flex-col rounded-2xl border border-gray-300 bg-white shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition-all">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* Attachment chip */}
          {attachedWorkflow && (
            <div className="px-4 pt-3 pb-0">
              <div className="inline-flex items-center gap-2 rounded-lg bg-purple-50 border border-purple-200 px-3 py-1.5 text-sm">
                <DocumentIcon className="h-4 w-4 text-purple-600" />
                <span className="text-purple-700 font-medium">{attachedWorkflow.filename}</span>
                <span className="text-purple-500">({attachedWorkflow.nodeCount} nodes)</span>
                <button
                  type="button"
                  onClick={onRemoveAttachment}
                  className="ml-1 rounded-full p-0.5 text-purple-400 hover:text-purple-600 hover:bg-purple-100"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Textarea area */}
          <div className="px-4 pt-4 pb-2">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your workflow..."
              rows={2}
              className="w-full resize-none border-none bg-transparent text-sm placeholder-gray-400 focus:outline-none focus:ring-0"
              disabled={disabled}
            />
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between px-3 pb-3">
            {/* Left toolbar group */}
            <div className="flex items-center gap-2">
{/* Mode dropdown */}
              <Dropdown
                value={mode}
                options={modeOptions}
                onChange={(v) => handleModeChange(v as ChatMode)}
                disabled={disabled}
                variant={modeVariant}
                icon={modeIcon}
              />

              {/* Model dropdown */}
              <Dropdown
                value={model}
                options={MODEL_OPTIONS}
                onChange={(v) => onModelChange(v as GenerationModel)}
                disabled={disabled}
                variant="outlined"
                icon={<SparklesIcon className="h-4 w-4" />}
                lockedOptions={lockedModels}
                onLockedClick={() => openSettingsModal('subscription')}
              />
            </div>

            {/* Right toolbar group */}
            <div className="flex items-center gap-2">
              {/* Deploy button - only show when there's a workflow */}
              {workflow && onDeploy && (
                <button
                  type="button"
                  onClick={handleDeploy}
                  disabled={isDeploying}
                  className="flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-800 disabled:opacity-50"
                >
                  {isDeploying ? (
                    <>
                      <SpinnerIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Deploying...</span>
                    </>
                  ) : (
                    <>
                      <RocketIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Deploy to n8n</span>
                    </>
                  )}
                </button>
              )}

              {/* Send button */}
              <button
                type="submit"
                disabled={!canSubmit}
                className={`flex items-center justify-center rounded-full p-2 transition-all duration-200 ${
                  canSubmit
                    ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <SpinnerIcon className="h-5 w-5" />
                ) : (
                  <ArrowUpIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* File error toast */}
          {fileError && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                <span>{fileError}</span>
                <button
                  type="button"
                  onClick={() => setFileError(null)}
                  className="ml-auto rounded-full p-0.5 text-red-400 hover:text-red-600"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
